import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Button, IconButton, TextField } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deleteWebhook, getWebhookList } from '../../../../services/manage/webhook';
import { RootState } from '../../../../store';
import DataTable from '../../../DataDisplay/Table';
import ContentLayout from '../../../Layout/Content';
import { showDialog, showToast } from '../../../utils/notifications';


const ManageWebhook = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const authContext = useSelector((state: RootState) => state.mainReducer.authContext);
    const region = useSelector((state: RootState) => state.mainReducer.region);
    const isManager = authContext?.is_group_manager || authContext?.role === 'Owner' || authContext?.role === 'SubOwner';
    const [webhookList, setWebhookList] = useState<any>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredData, setFilteredData] = useState<any[]>([]);

    const handleSearch = (query: string) => {
        setSearchQuery(query);
        if (!query.trim()) {
            setFilteredData(webhookList);
            return;
        }

        const filtered = webhookList.filter((item: any) => 
            item.name.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredData(filtered);
    };

    useEffect(() => {
        if(!isManager) {
            showDialog({
                title: t('manage_webhook_permission_dialog'),
                content: t('manage_webhook_permission_dialog_description'),
                okText: t('ok'),
                onOk: () => {
                    navigate('/settings/service');
                },
                cancelButtonProps: {
                    hidden: true,
                },
            });
            return;
        }
        const fetchWebhookList = async () => {
            try {
                const response = await getWebhookList(region, authContext.token);
                if (response?.result?.code === '0000') {
                    setWebhookList(response.data);
                    setFilteredData(response.data);
                }
            } catch (error) {
                console.error('Error fetching webhook list:', error);
            }
        };
        fetchWebhookList();
    }, []);

    
    useEffect(() => {
        setFilteredData(webhookList);
    }, [webhookList]);


    const handleDeleteWebhook = async (id: string) => {
        const response = await deleteWebhook(region, authContext.token, id);
        if (response?.result?.code === '0000') {
            setWebhookList(webhookList.filter((item: any) => item.id !== id));
            showToast('success', t('delete_webhook_success'));
        } else {
            showToast('error', t('delete_webhook_failed'));
        }
    };
    
    
    const columns: GridColDef[] = [
        { field: 'name', headerName: t('name'), flex: 1 },
        { field: 'url', headerName: t('webhook_url'), flex: 1 },
        {
            field: 'action',
            headerName: t('action'),
            flex: 1,
            renderCell: (params) => (
                <div className="flex gap-2 items-center h-full">
                    <IconButton onClick={() => navigate(`/settings/webhook/create/${params.row.id}`)}>
                        <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteWebhook(params.row.id)}>
                        <DeleteIcon />
                    </IconButton>
                </div>
            ),
        },
    ];


    return (
        <ContentLayout fullWidth={true} position="relative" overflow="hidden">
            <div className="flex justify-between gap-4">
                <div className="text-2xl font-medium">{t('manage_webhook')}</div>
                <div className="flex-1 max-w-[600px]">
                    <TextField 
                        fullWidth 
                        label={t('search')} 
                        placeholder={t('webhook_search_placeholder')} 
                        size="small"
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </div>
                <Button variant="contained" onClick={() => navigate('/settings/webhook/create')}>
                    {t('create_webhook')}
                </Button>
            </div>
            <div className="mt-20">
                <DataTable height={600} columns={columns} rows={filteredData} pageSize={10} />
            </div>
        </ContentLayout>
    );
};

export default ManageWebhook;
